import { useState } from "react";

export interface Filters {
  sort: "latest" | "hot";
  duration: "all" | "short" | "long";
  author: "all" | "official" | "user";
}

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

const sortOptions = [
  { value: "latest", label: "最新发布", icon: "ri-time-line" },
  { value: "hot", label: "最热播放", icon: "ri-fire-line" },
] as const;

const durationOptions = [
  { value: "all", label: "全部时长" },
  { value: "short", label: "短视频（3分钟内）" },
  { value: "long", label: "长视频（3分钟以上）" },
] as const;

const authorOptions = [
  { value: "all", label: "全部来源" },
  { value: "official", label: "官方出品" },
  { value: "user", label: "用户投稿" },
] as const;

export default function FilterBar({ filters, onChange }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const chip = (active: boolean) =>
    `h-9 px-4 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap border ${
      active
        ? "bg-primary-500 text-background-50 border-primary-500 font-semibold"
        : "bg-background-50 text-foreground-600 border-background-300 hover:border-primary-400 hover:text-primary-600"
    }`;

  const group = (
    label: string,
    options: readonly { value: string; label: string }[],
    current: string,
    key: keyof Filters,
  ) => (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-foreground-400 font-medium">{label}</span>
      <div className="flex items-center gap-2 flex-wrap">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ ...filters, [key]: opt.value } as Filters)}
            className={chip(current === opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* desktop */}
      <div className="hidden md:flex flex-wrap items-start gap-x-8 gap-y-4 p-4 rounded-lg bg-background-50 border border-background-200">
        {group("排序方式", sortOptions, filters.sort, "sort")}
        {group("视频时长", durationOptions, filters.duration, "duration")}
        {group("上传者类型", authorOptions, filters.author, "author")}
      </div>

      {/* mobile trigger */}
      <div className="md:hidden flex items-center gap-3">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line" aria-hidden="true"></i>
          筛选与排序
        </button>
        <span className="text-sm text-foreground-500">
          当前：
          {sortOptions.find((s) => s.value === filters.sort)?.label}
        </span>
      </div>

      {/* mobile sheet */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 animate-fade-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <div className="space-y-5">
              {group("排序方式", sortOptions, filters.sort, "sort")}
              {group("视频时长", durationOptions, filters.duration, "duration")}
              {group("上传者类型", authorOptions, filters.author, "author")}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  onChange({ sort: "latest", duration: "all", author: "all" });
                }}
                className="flex-1 h-11 rounded-full border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}